// The second sign-in step of administrators and researchers, in place of the login form: the
// 6-digit code the server emailed (mfaSession.ts). One field; typing, pasting or the browser's
// one-time-code autofill fills it, and a full code is checked at once.
import React, { useEffect, useRef, useState } from "react"
import {
  Box,
  Checkbox,
  CircularProgress,
  createStyles,
  Fab,
  FormControlLabel,
  InputBase,
  makeStyles,
  Fade,
} from "@material-ui/core"
import { useTranslation } from "react-i18next"
import { MfaError, mfaErrorMessage, mfaSignIn, mfaVerify } from "../mfaSession"

export type MfaCodeRequest = {
  serverAddress: string
  id: string
  password: string
  emailHint: string
  // seconds the code stays valid
  expiresIn: number
}

const DIGITS = 6
const RESEND_WAIT_S = 30
const BLUE = "#7599FF"
const LINK_BLUE = "#6083E7"
const RED = "#e5484d"
const GREEN = "#2f9e5a"

const useStyles = makeStyles(() =>
  createStyles({
    // The login form's width, so the page does not narrow when the step replaces it.
    root: { width: 320, maxWidth: "100%", paddingTop: 4, textAlign: "left", wordBreak: "keep-all" },
    title: { fontSize: 22, fontWeight: 700, color: "rgba(0, 0, 0, 0.85)", margin: "0 0 12px" },
    lead: { fontSize: 14, lineHeight: 1.6, color: "rgba(0, 0, 0, 0.6)", margin: "0 0 6px" },
    status: { fontSize: 14, lineHeight: 1.6, minHeight: 22, margin: "0 0 20px" },
    notice: { color: GREEN },
    error: { color: RED },
    label: { display: "block", fontSize: 14, fontWeight: 600, color: "rgba(0, 0, 0, 0.7)", marginBottom: 8 },
    field: {
      width: "100%",
      height: 50,
      borderRadius: 10,
      background: "#f5f5f5",
      border: "2px solid transparent",
      transition: "border-color 0.15s, background 0.15s",
      "& input": {
        padding: "0 14px",
        fontSize: 20,
        fontWeight: 700,
        letterSpacing: "0.3em",
        fontVariantNumeric: "tabular-nums",
        color: "rgba(0, 0, 0, 0.85)",
        caretColor: BLUE,
      },
    },
    focused: { borderColor: BLUE, background: "#fff" },
    invalid: { borderColor: RED, background: "#fff6f6" },
    remember: {
      // the checkbox lines up with the field's left edge
      margin: "12px 0 24px -6px",
      "& .MuiTypography-root": { fontSize: 14, color: "rgba(0, 0, 0, 0.7)" },
      "& .MuiCheckbox-root": { padding: 6 },
    },
    submit: {
      width: 200,
      "& .MuiFab-label": { textTransform: "none", fontSize: 16, fontWeight: "bold" },
    },
    links: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "center",
      rowGap: 8,
      marginTop: 24,
      fontSize: 13,
      whiteSpace: "nowrap",
    },
    divider: { width: 1, height: 12, background: "rgba(0, 0, 0, 0.15)", margin: "0 14px" },
    link: {
      background: "none",
      border: 0,
      padding: 0,
      font: "inherit",
      fontWeight: 600,
      color: LINK_BLUE,
      cursor: "pointer",
      "&:hover:not(:disabled)": { textDecoration: "underline" },
      "&:disabled": { color: "rgba(0, 0, 0, 0.35)", cursor: "default" },
    },
  })
)

export default function MfaCodeStep({
  request,
  onDone,
}: {
  request: MfaCodeRequest
  // true once the code was accepted (the session is stored), false to go back to the login form
  onDone: (verified: boolean) => void
}) {
  const classes = useStyles()
  const { t } = useTranslation()
  const [code, setCode] = useState("")
  const [focused, setFocused] = useState(false)
  const [remember, setRemember] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resent, setResent] = useState(false)
  const [expiresAt, setExpiresAt] = useState(Date.now() + request.expiresIn * 1000)
  const [resendAt, setResendAt] = useState(Date.now() + RESEND_WAIT_S * 1000)
  const [now, setNow] = useState(Date.now())
  const input = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])
  useEffect(() => input.current?.focus(), [])

  const expired = expiresAt <= now
  const resendIn = Math.max(0, Math.ceil((resendAt - now) / 1000))
  const complete = code.length === DIGITS

  const message = (e: any) =>
    (e instanceof MfaError && mfaErrorMessage(e.message, t)) || `${t("Some error occured. Please login again")}`

  const verify = async (value = code) => {
    if (busy || value.length !== DIGITS || expired) return
    setBusy(true)
    setError(null)
    try {
      await mfaVerify(request.serverAddress, request.id, request.password, value, remember)
      onDone(true)
    } catch (e) {
      setError(message(e))
      setCode("")
      setBusy(false)
      setTimeout(() => input.current?.focus())
    }
  }

  const enter = (value: string) => {
    const typed = value.replace(/\D/g, "").slice(0, DIGITS)
    if (error) setError(null)
    setCode(typed)
    if (typed.length === DIGITS) verify(typed)
  }

  const resend = async () => {
    if (busy || resendIn > 0) return
    setBusy(true)
    setError(null)
    try {
      const step: any = await mfaSignIn(request.serverAddress, request.id, request.password)
      setExpiresAt(Date.now() + (step?.expires_in ?? request.expiresIn) * 1000)
      setResendAt(Date.now() + RESEND_WAIT_S * 1000)
      setCode("")
      setResent(true)
      setTimeout(() => input.current?.focus())
    } catch (e) {
      setError(message(e))
    } finally {
      setBusy(false)
    }
  }

  const status = error ? (
    <span className={classes.error} role="alert">
      {error}
    </span>
  ) : expired ? (
    <span className={classes.error} role="alert">
      {`${t("The code has expired. Please request a new code.")}`}
    </span>
  ) : (
    <span className={classes.notice} role="status">
      {resent
        ? `${t("New code sent. If it is not in your inbox, check your spam folder.")}`
        : `${t("Email sent. If it is not in your inbox, check your spam folder.")}`}
    </span>
  )

  return (
    <Fade in timeout={250}>
      <Box className={classes.root}>
        <h2 className={classes.title}>{`${t("Verification code")}`}</h2>
        <p className={classes.lead}>
          {`${t("Enter the 6-digit code we emailed you within {{minutes}} minutes.", {
            minutes: Math.round(request.expiresIn / 60),
          })}`}
        </p>
        <p className={classes.status}>{status}</p>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            verify()
          }}
        >
          <label className={classes.label} htmlFor="mfa-code">{`${t("Code")}`}</label>
          <InputBase
            id="mfa-code"
            value={code}
            disabled={busy}
            className={`${classes.field} ${focused ? classes.focused : ""} ${error ? classes.invalid : ""}`}
            inputRef={input}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(event) => enter(event.target.value)}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: DIGITS,
              autoComplete: "one-time-code",
            }}
          />
          <FormControlLabel
            className={classes.remember}
            control={
              <Checkbox
                checked={remember}
                onChange={(event) => setRemember(event.target.checked)}
                size="small"
                style={{ color: remember ? BLUE : undefined }}
              />
            }
            label={`${t("Remember this device (no code for 30 days)")}`}
          />
          <Box textAlign="center">
            <Fab
              variant="extended"
              type="submit"
              className={classes.submit}
              disabled={busy || !complete || expired}
              style={busy || !complete || expired ? undefined : { background: BLUE, color: "white" }}
            >
              {busy ? <CircularProgress size={22} style={{ color: "white" }} /> : `${t("Verify")}`}
            </Fab>
          </Box>
        </form>
        <Box className={classes.links}>
          <button type="button" className={classes.link} onClick={resend} disabled={busy || resendIn > 0}>
            {resendIn > 0 ? `${t("Resend code ({{seconds}}s)", { seconds: resendIn })}` : `${t("Resend code")}`}
          </button>
          <span className={classes.divider} />
          <button type="button" className={classes.link} onClick={() => onDone(false)} disabled={busy}>
            {`${t("Use another account")}`}
          </button>
        </Box>
      </Box>
    </Fade>
  )
}
